import { useState } from 'react';
import {
  Container,
  Stack,
  Group,
  TextInput,
  Text,
  ScrollArea,
  Button,
} from '@mantine/core';
import { useSchemaForm } from '@ayocore/platform/client/hooks/useSchemaForm';
import partnerSchemaDefinition from '../../../../schemas/partner.schema';
import type { Partner } from '../../../../schemas/partner.schema';
import { usePartnerRepository } from '@ayocore/erp/crm/client/rpc';
import { CompanyAutocomplete } from './CompanyAutocomplete';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import { ExternalCompanySearchView } from '@ayocore/erp/crm/client/components/ExternalCompanySearchView';
import { companyInfoToFormDto } from '@ayocore/erp/crm/common/types/company';
import type { CompanyInfo } from '@ayocore/erp/crm/common/types/company';
import { userRepository } from '@ayocore/platform/identity/client/rpc';

/**
 * CreatePartnerForm - Comprehensive partner creation form using SchemaForm
 *
 * Features:
 * - All field types from PartnerCreateSchema (string, email, phone, enum, dates)
 * - Automatic validation from TypeBox schema
 * - Business logic integration via PartnerRepository
 * - Excellent UX with submit-triggered validation
 * - Zero rerenders during typing for perfect performance
 */
export interface CreatePartnerFormProps extends Record<string, unknown> {
  onPartnerCreated?: (partnerId?: number, partnerName?: string) => void;
}

export function CreatePartnerForm({ onPartnerCreated }: CreatePartnerFormProps) {
  // Initialize SchemaForm with auto-generated createSchema from defineSchema
  const SchemaForm = useSchemaForm(partnerSchemaDefinition.createSchema, {
    hideDescriptions: true,
  });
  const partnerRepository = usePartnerRepository();
  const searchModal = useModal(ExternalCompanySearchView);
  type CreatePartnerExtended = Parameters<typeof partnerRepository.createExtended>[0];

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [showRequisites, setShowRequisites] = useState(false);

  // Статус клиента рассчитывается автоматически (grasias_recalculate_partner_status).

  // Обработка сабмита формы партнёра
  const handleSubmit = async (_values: typeof SchemaForm.values) => {
    const v = SchemaForm.values as Partial<Partner>;

    const payload: CreatePartnerExtended = {
      name: String(v.name ?? ''),
      full_name: v.full_name ?? null,
      address: v.address ?? null,
      director: v.director ?? null,
      inn: v.inn ?? null,
      ogrn: v.ogrn ?? null,
      notes: v.notes ?? null,
      manager_id: (v as { manager_id?: number | null }).manager_id ?? null,
      customer_status_id: (v as { customer_status_id?: number | null }).customer_status_id ?? null,
      sales_area_id: (v as { sales_area_id?: number | null }).sales_area_id ?? null,
      linked_id: v.linked_id ?? null,
      // Даты контактов — явно приводим к null, чтобы удовлетворить схему
      first_contact_date: (v as { first_contact_date?: Date | null }).first_contact_date ?? null,
      last_contact_date: (v as { last_contact_date?: Date | null }).last_contact_date ?? null,
      next_contact_date: (v as { next_contact_date?: Date | null }).next_contact_date ?? null,
      // Contact channels (optional)
      email: email.trim() || null,
      phone: phone.trim() || null,
      website: website.trim() || null,
    };

    // Create partner using extended repository method (includes business logic hooks)
    // Если будет ошибка - пробрасываем её дальше, чтобы useSchemaForm обработал её
    const newPartner = await partnerRepository.createExtended(payload);

    // Success feedback - устанавливаем кастомное сообщение вместо дефолтного
    SchemaForm.setSuccess(`Партнер "${newPartner.name}" успешно создан! ID: ${newPartner.id}`);

    // Reset form for next partner
    SchemaForm.reset();
    setEmail('');
    setPhone('');
    setWebsite('');

    // Call the callback to refresh list and close modal after delay
    setTimeout(() => {
      if (onPartnerCreated) {
        onPartnerCreated(newPartner.id, newPartner.name);
      }
    }, 1500);

    return newPartner;
  };

  // Handle form reset/cancel
  const handleCancel = () => {
    SchemaForm.reset();
    SchemaForm.clearMessages();
    if (onPartnerCreated) {
      onPartnerCreated();
    }
  };

  // Handle company selection from ExternalCompanySearchView
  const handleCompanySelect = (company: CompanyInfo) => {
    const dto = companyInfoToFormDto(company);
    SchemaForm.setFieldValue('name', dto.short_name);
    SchemaForm.setFieldValue('full_name', dto.full_name ?? '');
    SchemaForm.setFieldValue('inn', dto.inn);
    SchemaForm.setFieldValue('ogrn', dto.ogrn ?? '');
    SchemaForm.setFieldValue('address', dto.legal_address ?? '');
    SchemaForm.setFieldValue('director', company.director ?? '');
    searchModal.close();
  };

  const openSearch = () => {
    searchModal.open(
      {
        title: 'Найти компанию',
        initialQuery: String(SchemaForm.values.name ?? '').trim(),
        onSelect: handleCompanySelect,
      },
      { size: 'lg', background: 'glassmorphism' }
    );
  };

  return (
    <form
      onSubmit={SchemaForm.handleSubmit(handleSubmit)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Scrollable Form Content */}
      <ScrollArea.Autosize flex={1} mih={0} scrollbarSize={0}>
        <Container size='sm' px={{ base: 'xs', sm: 'sm' }} py='xs' maw='100%'>
          <Stack gap='xs'>
            {/* Messages for feedback */}
            <SchemaForm.Messages />

            {/* Basic Information */}
            <Stack gap='xs'>
              <CompanyAutocomplete
                value={String(SchemaForm.values.name ?? '')}
                onChange={value => SchemaForm.setFieldValue('name', value)}
                onPartnerSelect={partner =>
                  onPartnerCreated?.(partner.id, String(partner.name ?? ''))
                }
                onOpenSearch={openSearch}
                label='Название компании'
                placeholder='Название компании'
                size='sm'
                error={
                  SchemaForm.showValidationErrors && SchemaForm.errors.name
                    ? String(SchemaForm.errors.name)
                    : undefined
                }
              />
              <SchemaForm.Field.notes size='sm' />
              <SchemaForm.Field.manager_id
                size='sm'
                label='Менеджер'
                repo={userRepository}
                displayField='name'
                placeholder='Выберите менеджера...'
              />

              <Group grow>
                <TextInput
                  size='sm'
                  label='Email'
                  placeholder='work@example.com'
                  value={email}
                  onChange={event => setEmail(event.currentTarget.value)}
                  autoComplete='new-password'
                />
                <TextInput
                  size='sm'
                  label='Телефон'
                  placeholder='+7 999 123-45-67'
                  value={phone}
                  onChange={event => setPhone(event.currentTarget.value)}
                  autoComplete='new-password'
                />
              </Group>

              <TextInput
                size='sm'
                label='Сайт'
                placeholder='https://example.com'
                value={website}
                onChange={event => setWebsite(event.currentTarget.value)}
                autoComplete='new-password'
              />
            </Stack>

            {/* Address & Location */}
            <SchemaForm.Field.address size='sm' />

            {/* Business Details (director + INN/OGRN) */}
            <Stack gap='xs'>
              <Text
                size='sm'
                c='blue'
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setShowRequisites(prev => !prev)}
              >
                {showRequisites ? 'Скрыть реквизиты (ИНН / ОГРН)' : 'Указать ИНН / ОГРН'}
              </Text>

              {showRequisites && (
                <Stack gap='xs'>
                  <SchemaForm.Field.director size='sm' />
                  <Group grow>
                    <SchemaForm.Field.inn size='sm' />
                    <SchemaForm.Field.ogrn size='sm' />
                  </Group>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Container>
      </ScrollArea.Autosize>

      {/* Fixed Footer with Action Buttons */}
      <div
        style={{
          borderTop: '1px solid #e9ecef',
          backgroundColor: 'transparent',
          padding: '8px 0 0',
          flexShrink: 0,
        }}
      >
        <Group justify='space-between'>
          <Button
            variant='light'
            color='red'
            size='sm'
            fw={400}
            onClick={() => {
              SchemaForm.reset();
              setEmail('');
              setPhone('');
              setWebsite('');
            }}
          >
            Очистить форму
          </Button>
          <Group gap='xs'>
            <SchemaForm.buttons.cancel onClick={handleCancel} label='Отмена' size='sm' />
            <SchemaForm.buttons.submit label='Создать партнера' size='sm' />
          </Group>
        </Group>
      </div>
    </form>
  );
}

export default CreatePartnerForm;
